import {useState} from 'react';
import {EnvelopeIcon, LinkIcon, UserPlusIcon} from "@heroicons/react/24/outline";

import {User} from "../../../../../api/accessControl/User.ts";
import {Input} from "../../../../../components/general/Input.tsx";
import {PrimaryButton} from "../../../../../components/general/PrimaryButton.tsx";
import {ComboboxList} from "../../../../../components/general/ComboboxList.tsx";
import Modal from "../../../../../components/general/Modal.tsx";
import {useBusinessAccess} from "./useBusinessAccess.ts";

interface EntrepreneurOption {
    id: string;
    displayName: string;
}

const ROLE_LABELS: Record<string, string> = {
    entrepreneur: 'Ondernemer',
    partner: 'Boekhouder',
    admin: 'Beheerder',
};

function formatDate(isoDate: string): string {
    return new Date(isoDate).toLocaleDateString('nl-NL', {day: 'numeric', month: 'short', year: 'numeric'});
}

interface BusinessAccessModalProps {
    currentUser: User;
    businessId: string;
    open: boolean;
    updateOpenState: (open: boolean) => void;
    onAccessChanged?: () => void;
}

export default function BusinessAccessModal(
    {currentUser, businessId, open, updateOpenState, onAccessChanged}: BusinessAccessModalProps
) {
    const {
        isLoading,
        members,
        pendingInvitations,
        entrepreneurs,
        inviteEntrepreneur,
        linkEntrepreneur,
        resendInvitation,
    } = useBusinessAccess(currentUser, businessId, onAccessChanged);

    const [mode, setMode] = useState<'new' | 'existing'>('new');
    const [email, setEmail] = useState<string>('');
    const [selectedEntrepreneurId, setSelectedEntrepreneurId] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [resendingEmail, setResendingEmail] = useState<string | null>(null);

    const entrepreneurOptions: EntrepreneurOption[] = entrepreneurs.map((entrepreneur) => ({
        id: entrepreneur.id,
        displayName: `${entrepreneur.first_name} ${entrepreneur.last_name} (${entrepreneur.businesses.map((b) => b.business_name).join(', ')})`,
    }));

    async function handleInvite() {
        setIsSubmitting(true);
        const success = await inviteEntrepreneur(email);
        if (success) setEmail('');
        setIsSubmitting(false);
    }

    async function handleLink() {
        if (!selectedEntrepreneurId) return;
        setIsSubmitting(true);
        const success = await linkEntrepreneur(selectedEntrepreneurId);
        if (success) setSelectedEntrepreneurId(null);
        setIsSubmitting(false);
    }

    async function handleResend(invitationEmail: string) {
        if (resendingEmail) return;
        setResendingEmail(invitationEmail);
        await resendInvitation(invitationEmail);
        setResendingEmail(null);
    }

    return (
        <Modal open={open} updateOpenState={updateOpenState} title="Toegang">
            <div className="space-y-8">
                {/* Ondernemer toevoegen */}
                <div className="rounded-xl border border-gray-200 p-5 shadow-sm">
                    <h3 className="text-base font-semibold text-gray-900">Ondernemer toevoegen</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        Nodig een nieuwe ondernemer uit per e-mail, of koppel iemand uit je andere klanten.
                    </p>

                    <div className="mt-4 inline-flex rounded-lg bg-gray-100 p-1">
                        <button
                            type="button"
                            aria-pressed={mode === 'new'}
                            onClick={() => setMode('new')}
                            className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition ${
                                mode === 'new' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <UserPlusIcon className="h-4 w-4"/>
                            Nieuwe uitnodigen
                        </button>
                        <button
                            type="button"
                            aria-pressed={mode === 'existing'}
                            onClick={() => setMode('existing')}
                            className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition ${
                                mode === 'existing' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <LinkIcon className="h-4 w-4"/>
                            Bestaande koppelen
                        </button>
                    </div>

                    <div className="mt-4">
                        {mode === 'new' ? (
                            <div className="flex items-end gap-2">
                                <div className="flex-1">
                                    <Input
                                        type="email"
                                        placeholder="email@adres.nl"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                    />
                                </div>
                                <PrimaryButton title="Uitnodigen" icon={UserPlusIcon} onClick={handleInvite} disabled={isSubmitting} className="h-[38px]"/>
                            </div>
                        ) : (
                            <div className="flex items-end gap-2">
                                <div className="flex-1">
                                    <ComboboxList<EntrepreneurOption>
                                        anchor={{to: 'bottom start', gap: 4}}
                                        menuWidth="w-[var(--input-width)]"
                                        openMenuOnFocus={true}
                                        comboBoxClassName="w-full min-w-0"
                                        className="block h-[38px] w-full rounded-md bg-white px-3 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 outline-none placeholder:text-gray-400"
                                        emptySelectionMessage="Zoek een ondernemer..."
                                        options={entrepreneurOptions}
                                        selected={entrepreneurOptions.find((o) => o.id === selectedEntrepreneurId) ?? null}
                                        onSelect={(option) => setSelectedEntrepreneurId(option ? option.id : null)}
                                        internalMapper={(item) => item}
                                        showClearButton={true}
                                    />
                                </div>
                                <PrimaryButton title="Koppelen" icon={LinkIcon} onClick={handleLink} disabled={isSubmitting || !selectedEntrepreneurId} className="h-[38px]"/>
                            </div>
                        )}
                    </div>
                </div>

                {/* Leden */}
                <div>
                    <h3 className="text-base font-semibold text-gray-900 mb-3">Leden</h3>
                    {isLoading ? (
                        <p className="text-sm text-gray-500">Laden...</p>
                    ) : members.length === 0 ? (
                        <p className="text-sm text-gray-500">Nog geen leden.</p>
                    ) : (
                        <ul className="divide-y divide-gray-200 border border-gray-200 rounded-md">
                            {members.map((member) => (
                                <li key={member.id} className="flex items-center gap-3 p-3">
                                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-superliteprimary text-xs font-semibold text-primary">
                                        {member.first_name.charAt(0).toUpperCase()}{member.last_name.charAt(0).toUpperCase()}
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-gray-900">
                                            {member.first_name} {member.last_name}
                                            <span className="ml-2 text-xs font-normal text-gray-500">
                                                {ROLE_LABELS[member.role] ?? member.role}
                                            </span>
                                        </p>
                                        <p className="text-sm text-gray-500">{member.email}</p>
                                        {member.other_businesses.length > 0 && (
                                            <p className="text-xs text-gray-400 mt-0.5">
                                                Ook in: {member.other_businesses.map((b) => b.business_name).join(', ')}
                                            </p>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Openstaande uitnodigingen */}
                <div>
                    <h3 className="text-base font-semibold text-gray-900 mb-3">Openstaande uitnodigingen</h3>
                    {isLoading ? (
                        <p className="text-sm text-gray-500">Laden...</p>
                    ) : pendingInvitations.length === 0 ? (
                        <p className="text-sm text-gray-500">Geen openstaande uitnodigingen.</p>
                    ) : (
                        <ul className="divide-y divide-gray-200 border border-gray-200 rounded-md">
                            {pendingInvitations.map((invitation) => (
                                <li key={`${invitation.email}-${invitation.created_at}`} className="flex items-center justify-between gap-3 p-3">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <EnvelopeIcon className="h-5 w-5 text-gray-400 flex-shrink-0"/>
                                        <div className="min-w-0">
                                            <p className="text-sm text-gray-900">{invitation.email}</p>
                                            <p className="text-xs text-gray-400">Verstuurd {formatDate(invitation.created_at)}</p>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => handleResend(invitation.email)}
                                        disabled={resendingEmail !== null}
                                        className="text-sm font-medium text-primary hover:underline flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
                                    >
                                        {resendingEmail === invitation.email ? 'Versturen...' : 'Opnieuw versturen'}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </Modal>
    );
}
